import {
  createAsyncThunk,
  createSelector,
  createSlice,
  current,
  PayloadAction,
} from '@reduxjs/toolkit';

import { RootState } from '../../../store';
import { generateBoilerplateAsyncThunkMatchers } from '../../../store/helpers';
import { logoutUserThunk } from '../../auth/api/authSlice';
import { Message } from '../types';
import {
  getMessageByMailboxId,
  getMessages,
  updateMailboxMessage,
} from '../../../api/services/MessageService';

const ACTIONS_PREFIX = 'messages/inbox';

export const getMessagesThunk = createAsyncThunk(`${ACTIONS_PREFIX}/getMessages`, getMessages);

export const getMessageByIdThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getMessageById`,
  getMessageByMailboxId,
);

export const updateMailboxMessageThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/updateMailboxMessage`,
  updateMailboxMessage,
);

export interface InboxState {
  messages: { resource: Message }[];
  isComposing: boolean;
  total: number;
  totalUnreadInboxMessages: number;
  activeMessage: Message | any;
  messageThreadActive: boolean;
  forceRefetch: boolean;
  status: 'pending' | 'success' | 'failure';
  isLoading: boolean;
  error?: string;
}

const initialState = {
  messages: [],
  isComposing: false,
  total: 0,
  totalUnreadInboxMessages: 0,
  activeMessage: undefined,
  messageThreadActive: false,
  forceRefetch: false,
  status: 'success',
  isLoading: false,
};

const inboxSlice = createSlice({
  name: 'inbox',
  initialState: initialState as InboxState,
  reducers: {
    setIsComposing: (state, action: PayloadAction<boolean>) => {
      state.isComposing = action.payload;
    },
    setMessageThreadActive: (state, action: PayloadAction<boolean>) => {
      state.messageThreadActive = action.payload;
    },
    removeOldThreadMessage: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter(
        (message: any) => message.resource.id !== action.payload,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMessagesThunk.fulfilled, (state, action) => {
      state.totalUnreadInboxMessages = action.payload?.resource?.totalUnread || 0;
      state.total = action.payload?.resource?.total || 0;

      const currentMessages = current(state.messages);
      const entries = (action.payload?.resource?.entry || []).map((e) => {
        if (!e.resource?.threadId) {
          return {
            ...e,
            resource: { ...e.resource, threadId: e.resource.messageId, hasReply: false },
          };
        }
        return { ...e, resource: { ...e.resource, hasReply: true } };
      });

      entries.forEach((entry) => {
        const index = currentMessages.findIndex(
          (messages) => messages.resource.threadId === entry.resource.threadId,
        );
        if (index === -1) {
          state.messages.push(entry);
        } else if (
          // * Check if the timestamp or read properties have changed
          entry.resource.message?.timestamp !==
            currentMessages[index].resource.message?.timestamp ||
          entry.resource.read !== currentMessages[index].resource.read
        ) {
          state.messages[index] = entry;
        }
      });

      state.messages.sort((a: any, b: any) => {
        return (
          new Date(b.resource.message.timestamp).valueOf() -
          new Date(a.resource.message.timestamp).valueOf()
        );
      });
    });
    builder.addCase(getMessageByIdThunk.fulfilled, (state, action) => {
      const message = action.payload.resource as Message;
      state.activeMessage = message;
    });
    builder.addCase(logoutUserThunk.fulfilled, (state) => {
      state.messages = [];
      state.totalUnreadInboxMessages = 0;
      state.total = 0;
      state.activeMessage = undefined;
      state.messageThreadActive = false;
    });
    generateBoilerplateAsyncThunkMatchers<InboxState>(builder, ACTIONS_PREFIX);
  },
});

// SELECTORS
export const selectInboxState = (state: RootState) => state.messages.inbox;
export const selectMessageByThreadId = (threadId?: string) =>
  createSelector(selectInboxState, (inboxState: InboxState) =>
    threadId
      ? inboxState.messages.find((m) => m.resource.threadId === threadId)?.resource
      : undefined,
  );

// ACTIONS
export const { setIsComposing, setMessageThreadActive, removeOldThreadMessage } =
  inboxSlice.actions;
// REDUCER
export default inboxSlice.reducer;
